// hook to track user's page visits
// only done on client pages
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@context/shared/AuthContext";
import apiClient from "@services/api/apiClient";
import API from "@config/shared/api-paths";
import { isAccreditor } from "@config/shared/roles";

const SESSION_KEY = "client_session_id";

function getOrCreateSessionId() {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored) return parseInt(stored, 10);

  const arr = new Int32Array(1);
  crypto.getRandomValues(arr);
  const id = Math.abs(arr[0]) || 1;
  sessionStorage.setItem(SESSION_KEY, String(id));
  return id;
}

export function usePageTracking() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const prevPathRef = useRef(null);
  const entryTimeRef = useRef(null);

  // Updated synchronously during render so the location effect always reads fresh values
  const authRef = useRef({ user, isAuthenticated });
  authRef.current = { user, isAuthenticated };

  useEffect(() => {
    const { isAuthenticated: authed, user: currentUser } = authRef.current;
    // The view-audits endpoint 403s for accreditors, and their confined
    // /accreditation-only navigation isn't the kind of usage this tracks.
    if (!authed || !currentUser?.id || isAccreditor(currentUser.roleId)) return;

    const path = location.pathname;
    if (path.startsWith("/auth/") || path.startsWith("/admin")) return;
    if (path === prevPathRef.current) return;

    const sessionID = getOrCreateSessionId();
    const now = Date.now();

    const payload = {
      userID: currentUser.id,
      sessionID,
      path,
      event: "page_view",
    };

    if (prevPathRef.current !== null && entryTimeRef.current !== null) {
      payload.duration = now - entryTimeRef.current;
    }

    apiClient
      .post(`${API.BASE}${API.FRONTEND.VIEW_AUDITS}`, payload)
      .catch(() => {});

    prevPathRef.current = path;
    entryTimeRef.current = now;
  }, [location.pathname, isAuthenticated]);
}
